import { useState, useEffect } from "react";
import Modal from "@modalSrc/Modal";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import DateTimePicker from "@elements/DateTimePicker";

type FormErrors = Record<string, string | null>;

export default function CreateInvoicesBulkModal(props) {
    const { isOpen, onClose, updateSpinner } = props;
    const [invoiceDate, setInvoiceDate] = useState('');
    const [errors, setErrors] = useState<FormErrors>({});
    const [pendingDates, setPendingDates] = useState<any[]>([]);
    const [selectedDateIndex, setSelectedDateIndex] = useState<number | null>(null);

    useEffect(() => {
        if (isOpen) {
            // Calculate default invoice date based on current date
            const today = new Date();
            const currentDay = today.getDate();
            let defaultDate;

            if (currentDay > 15) {
                // If current day > 15, set to first of next month
                defaultDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
            } else {
                // If current day <= 15, set to first of current month
                defaultDate = new Date(today.getFullYear(), today.getMonth(), 1);
            }

            setInvoiceDate(defaultDate.toISOString().split('T')[0]);
            setErrors({});
            setPendingDates([]);
            setSelectedDateIndex(null);

            // Fetch pending invoice dates
            loadPendingInvoiceDatesAsync();
        } else {
            // Reset form when modal closes
            setInvoiceDate('');
            setErrors({});
            setPendingDates([]);
            setSelectedDateIndex(null);
        }
    }, [isOpen]);

    async function loadPendingInvoiceDatesAsync() {
        try {
            updateSpinner && updateSpinner(true);
            const resp = await RightTimeApi.getAsync('/PendingInvoiceDates');
            if (resp && resp.ok && resp.jsonData) {
                setPendingDates(resp.jsonData);
            }
        } catch (error) {
            console.error("Error loading pending invoice dates:", error);
            toast.error("Error loading pending invoice dates");
        } finally {
            updateSpinner && updateSpinner(false);
        }
    }

    function handleSelectDate(date, index) {
        setInvoiceDate(date);
        setSelectedDateIndex(index);
    }

    if (!isOpen) return null;

    function validateForm() {
        const newErrors: FormErrors = {};

        if (!invoiceDate) {
            newErrors.invoiceDate = 'Invoice date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    async function handleCreate() {
        if (validateForm()) {
            try {
                updateSpinner && updateSpinner(true);

                const resp = await RightTimeApi.postAsync('/InvoiceCreateAll', {
                    invoiceDate: new Date(invoiceDate).toISOString()
                });
                if (resp && resp.ok) {
                    const count = resp.jsonData?.count ?? 0;
                    if (count === 0) {
                        toast.info("No invoices were created. Pending items may already be invoiced, have zero totals, or fall outside the selected period.");
                    } else {
                        toast.success(`Successfully created ${count} invoice${count !== 1 ? 's' : ''}`);
                    }
                    onClose();
                } else {
                    toast.error("Failed to create bulk invoices");
                    updateSpinner && updateSpinner(false);
                }
            } catch (error) {
                console.error("Error creating bulk invoices:", error);
                toast.error("Error creating bulk invoices");
                updateSpinner && updateSpinner(false);
            }
        }
    }

    const footer = (
        <>
            <div className="footerLeft">
            </div>
            <div className="footerRight">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCreate}
                    disabled={!invoiceDate}
                >
                    Create Invoices
                </button>
            </div>
        </>
    );

    return (
        <Modal
            isOpen={isOpen}
            title="Create Invoices in Bulk"
            onClose={onClose}
            footer={footer}
            className="createInvoicesBulkModal"
        >
            <div className="modalBody">

                <div className="formGroup">
                    <label htmlFor="invoiceDate">Invoice Date *</label>
                    <DateTimePicker
                        id="invoiceDate"
                        field="invoiceDate"
                        pickerTitle="Invoice Date"
                        value={invoiceDate}
                        onChange={(e) => setInvoiceDate(e?.target?.value || '')}
                        className={errors.invoiceDate ? 'error' : ''}
                        autoFocus
                        dateOnly
                    />
                    {errors.invoiceDate && <span className="errorText">{errors.invoiceDate}</span>}
                </div>


                {pendingDates.length > 0 && (
                    <div className="tableWrapper dimensionTable scrollable" style={{ maxHeight: '500px' }}>
                        <table >
                            <caption>
                              <span className="title">Pending Invoice Dates</span>
                            </caption>
                            <thead>
                                <tr>
                                    <th>Invoice Date</th>
                                    <th>Item Count</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pendingDates.map((item, index) => (
                                    <tr
                                        key={index}
                                        className={selectedDateIndex === index ? 'selected' : ''}
                                        onClick={() => handleSelectDate(new Date(item.date).toISOString().split('T')[0], index)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td>{new Date(item.date).toLocaleDateString()}</td>
                                        <td>{item.count}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}


            </div>
        </Modal>
    );
}

